import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import PersonIcon from '@mui/icons-material/Person';
import {Avatar, Box, Button, CircularProgress, DialogActions, DialogContent, DialogTitle, Stack} from '@mui/material';
import {useCallback, useRef, useState} from 'react';
import {Crop, PercentCrop, PixelCrop, ReactCrop, centerCrop, makeAspectCrop} from 'react-image-crop';
import {useDebouncedCallback} from 'use-debounce';
import {generateCanvasPreview, generateImageBlob} from '../canvas/canvasUtils';
import {CancelButton} from '../common/button/CancelButton';
import {TextButton} from '../common/button/TextButton';
import {Translation} from '../i18n/Translation';
import {HiddenInputField} from '../input/HiddenInputField';
import {ComponentWithoutChildren} from '../react/types';
import {ResponsiveDialog, ResponsiveDialogProps} from '../responsive/ResponsiveDialog';
import {useAvatarUpload} from './hooks/useAvatarUpload';

import 'react-image-crop/dist/ReactCrop.css';

const AvatarPreviewSizePixels = 40;
const AspectRatio = 1; // Require avatars to be square
const ImageExtension = 'png';

type AllowedDialogProps = Omit<ResponsiveDialogProps, 'onClose'>;
export interface EditAvatarDialogProps extends AllowedDialogProps {
  handleCancel: () => void;
  handleSelect: (url: string) => Promise<void>;
  userId: string;
}

export const EditAvatarDialog: ComponentWithoutChildren<EditAvatarDialogProps> = ({
  handleCancel,
  handleSelect,
  maxWidth = 'xs',
  userId,
  ...dialogProps
}: EditAvatarDialogProps) => {
  const avatarPreviewRef = useRef<HTMLCanvasElement>(null);
  const [previewAvailable, setPreviewAvailable] = useState(false);
  const [imgSrc, setImgSrc] = useState('');
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const imgRef = useRef<HTMLImageElement>(null);
  const {percentComplete, uploadFile} = useAvatarUpload(userId, ImageExtension);
  const [saveInProgress, setSaveInProgress] = useState(false);
  const cropToSave = completedCrop ?? crop;
  const cropCanBeSaved = !!cropToSave && /* v8 ignore next -- @preserve */ !saveInProgress;

  /* v8 ignore next -- @preserve */
  const onPreviewGenerated = useCallback(() => setPreviewAvailable(true), [setPreviewAvailable]);

  const reset = useCallback(() => {
    setCrop(undefined);
    setCompletedCrop(undefined);
    setImgSrc('');
    setPreviewAvailable(false);
    setSaveInProgress(false);
  }, [setCrop, setCompletedCrop, setImgSrc, setPreviewAvailable, setSaveInProgress]);

  const onSelectFile = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      /* v8 ignore next -- @preserve */
      if (e.target.files?.length) {
        setCrop(undefined);
        const reader = new FileReader();
        reader.addEventListener('load', () => setImgSrc(reader.result as string));
        reader.readAsDataURL(e.target.files[0]);
      }
    },
    [setCrop, setImgSrc],
  );

  const centerAspectCrop = useCallback((mediaWidth: number, mediaHeight: number, aspect: number) => {
    return centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: 90,
        },
        aspect,
        mediaWidth,
        mediaHeight,
      ),
      mediaWidth,
      mediaHeight,
    );
  }, []);

  const onImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const {width, height} = e.currentTarget;
      setCrop(centerAspectCrop(width, height, AspectRatio));
    },
    [centerAspectCrop, setCrop],
  );

  const generateCanvasPreviewCallback = useCallback(
    (pixelCrop: PixelCrop) => {
      /* v8 ignore next -- @preserve */
      if (pixelCrop.width && pixelCrop.height && imgRef.current && avatarPreviewRef.current) {
        generateCanvasPreview(imgRef.current, avatarPreviewRef.current, pixelCrop);
        onPreviewGenerated();
      }
    },
    [imgRef, onPreviewGenerated, avatarPreviewRef],
  );

  const debouncedGenerateCanvasPreview = useDebouncedCallback(generateCanvasPreviewCallback, 100);

  const cropOnChange = useCallback(
    (_pixelCrop: PixelCrop, percentCrop: PercentCrop) => setCrop(percentCrop),
    [setCrop],
  );

  const cropOnComplete = useCallback(
    (pixelCrop: PixelCrop) => {
      const isFirstCrop = !completedCrop;
      setCompletedCrop(pixelCrop);
      /* v8 ignore next -- @preserve */
      if (isFirstCrop) {
        // If this is the first crop, generate the preview immediately
        generateCanvasPreviewCallback(pixelCrop);
      } else {
        // Debounce any subsequent crops
        debouncedGenerateCanvasPreview(pixelCrop);
      }
    },
    [completedCrop, debouncedGenerateCanvasPreview, generateCanvasPreviewCallback, setCompletedCrop],
  );

  const saveCrop = useCallback(() => {
    async function doSave(): Promise<void> {
      setSaveInProgress(true);
      try {
        const image = imgRef.current;
        const previewCanvas = avatarPreviewRef.current;
        const blob: Blob = await generateImageBlob(image!, previewCanvas!, cropToSave!);
        const avatarUrl = await uploadFile(blob, {contentType: blob.type});
        await handleSelect(avatarUrl);
      } finally {
        reset();
      }
    }
    void doSave();
  }, [avatarPreviewRef, cropToSave, handleSelect, imgRef, reset, setSaveInProgress, uploadFile]);

  return (
    <ResponsiveDialog {...dialogProps} maxWidth={maxWidth} onClose={handleCancel}>
      <DialogTitle>
        <Stack direction="row" spacing="1rem" sx={{alignItems: 'center'}}>
          <Avatar>
            {/* v8 ignore start -- @preserve */}
            <PersonIcon sx={{display: previewAvailable ? 'none' : 'inherit'}} />
            <canvas
              ref={avatarPreviewRef}
              style={{
                display: previewAvailable ? 'inherit' : 'none',
                height: AvatarPreviewSizePixels,
                objectFit: 'contain',
                width: AvatarPreviewSizePixels,
              }}
            />
            {/* v8 ignore stop */}
          </Avatar>
          <Translation keys="user:avatar.edit.label">{(text) => <span>{text}</span>}</Translation>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{alignItems: 'center', display: 'flex', justifyContent: 'center'}}>
        {imgSrc ? (
          <ReactCrop
            aspect={AspectRatio}
            crop={crop}
            disabled={saveInProgress}
            minHeight={AvatarPreviewSizePixels}
            minWidth={AvatarPreviewSizePixels}
            onChange={cropOnChange}
            onComplete={cropOnComplete}
          >
            <img alt="Cropper" onLoad={onImageLoad} ref={imgRef} src={imgSrc} style={{maxWidth: '100%'}} />
          </ReactCrop>
        ) : (
          <Box sx={{dipsplay: 'flex'}}>
            <Button
              component="label"
              role={undefined}
              startIcon={<InsertPhotoIcon />}
              tabIndex={-1}
              variant="contained"
            >
              <Translation keys="user:avatar.edit.button.choose">
                {(text) => (
                  <span>
                    {text}
                    <HiddenInputField accept="image/*" aria-label={text} onChange={onSelectFile} type="file" />
                  </span>
                )}
              </Translation>
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <CancelButton disabled={saveInProgress} onClick={handleCancel} />
        <TextButton
          disabled={!cropCanBeSaved}
          i18nKey="user:avatar.edit.button.select"
          loading={saveInProgress}
          loadingIndicator={<CircularProgress value={percentComplete} variant="determinate" />}
          onClick={saveCrop}
          variant="contained"
        />
      </DialogActions>
    </ResponsiveDialog>
  );
};
